/**
 * ActiveCallWidget — renders in the Header when a call is active.
 * Subscribes to DOM events from client/lib/softphone.ts:
 *   - apex:call-update  (detail: ActiveCall | null)
 *   - apex:call-ended
 * Controls via window.ApexSoftphone.
 */

import { memo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  Play,
  ArrowRightLeft,
  Circle,
  PhoneIncoming,
  Check,
  X,
  Maximize2,
  Grid3x3,
} from 'lucide-react';
import { apiGet } from '../../hooks/useApi';

// Mirror the ActiveCall shape from client/lib/softphone.ts — the transfer
// payload's phone/identity field is `to` on that module, not `target`, and
// we stay aligned so drift doesn't silently leave the banner unrenderable.
interface ActiveCall {
  callId: string;
  phase: 'dialing' | 'ringing' | 'connected' | 'rejoining' | 'ended';
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  contactName?: string;
  contactId?: string;
  contactSelected?: boolean;
  held: boolean;
  muted: boolean;
  transfer: { type: 'cold' | 'warm'; to: string; name?: string } | null;
  startedAt: number;
  answeredAt?: number;
}

interface Softphone {
  hangup: () => void;
  answer: () => void;
  reject: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  showTransferDialog: () => void;
  sendDtmf: (digits: string) => Promise<void>;
  completeWarmTransfer: () => Promise<void>;
  cancelTransfer: () => Promise<void>;
  getActiveCall: () => ActiveCall | null;
  getRecordingState: () => 'idle' | 'recording' | 'paused' | 'stopped';
}

function getSoftphone(): Softphone | null {
  return (window as unknown as Record<string, unknown>)['ApexSoftphone'] as Softphone | null;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Standard telephone keypad layout — same digits/sub-letters the
// InCallModal keypad uses, kept in lockstep so both surfaces feel
// identical to the rep.
const KEYPAD_DIGITS: Array<{ digit: string; sub?: string }> = [
  { digit: '1' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: '*' },
  { digit: '0', sub: '+' },
  { digit: '#' },
];

/** Elapsed-time readout. Owns the 1s tick so only it re-renders per second. */
function CallElapsed({ answeredAt }: { answeredAt: number }) {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - answeredAt) / 1000));
  useEffect(() => {
    const tick = () => setElapsed(Math.floor((Date.now() - answeredAt) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [answeredAt]);
  return <>{formatDuration(elapsed)}</>;
}

export const ActiveCallWidget = memo(function ActiveCallWidget() {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [recording, setRecording] = useState<string>('idle');
  // Contacts sharing the caller's number — drives the ambiguous "N contacts"
  // label until the rep picks who's calling in the in-call modal.
  const [matchCount, setMatchCount] = useState(0);
  // DTMF keypad popover (#787) — collapsed by default so the compact
  // header strip stays compact; the rep expands it when an IVR / PIN /
  // extension prompt needs tones.
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [pressedDigits, setPressedDigits] = useState('');
  const keypadRef = useRef<HTMLDivElement>(null);

  // Subscribe to softphone DOM events
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail as ActiveCall | null;
      setCall(detail);
      const sp = getSoftphone();
      if (sp) setRecording(sp.getRecordingState());
    };
    const handleEnded = () => {
      setCall(null);
      setRecording('idle');
      // Collapse the keypad and clear the digit buffer so neither
      // survives into the next call.
      setKeypadOpen(false);
      setPressedDigits('');
    };

    window.addEventListener('apex:call-update', handleUpdate);
    window.addEventListener('apex:call-ended', handleEnded);

    // Check if there's already an active call (e.g. component remounted)
    const sp = getSoftphone();
    if (sp) {
      const existing = sp.getActiveCall();
      if (existing) {
        setCall(existing);
        setRecording(sp.getRecordingState());
      }
    }

    return () => {
      window.removeEventListener('apex:call-update', handleUpdate);
      window.removeEventListener('apex:call-ended', handleEnded);
    };
  }, []);

  // Count contacts sharing the caller's number so an ambiguous caller shows
  // "N contacts" rather than one arbitrary name. Same agency-scoped endpoint
  // the in-call modal picker uses; best-effort.
  const lookupPhone = call ? (call.direction === 'inbound' ? call.from : call.to) : '';
  useEffect(() => {
    if (!lookupPhone) {
      setMatchCount(0);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiGet<{ count: number }>(
          `/api/contacts/by-phone?phone=${encodeURIComponent(lookupPhone)}`,
        );
        if (!cancelled) setMatchCount(res.count ?? 0);
      } catch {
        // Best-effort — leave count at 0 so the single name shows.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lookupPhone]);

  // Close the keypad popover on outside click — same pattern as the
  // PhoneDialer dropdown.
  useEffect(() => {
    if (!keypadOpen) return;
    const handler = (e: MouseEvent) => {
      if (keypadRef.current && !keypadRef.current.contains(e.target as Node)) {
        setKeypadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [keypadOpen]);

  const hangup = useCallback(() => getSoftphone()?.hangup(), []);
  const answer = useCallback(() => getSoftphone()?.answer(), []);
  const reject = useCallback(() => getSoftphone()?.reject(), []);
  const toggleMute = useCallback(() => getSoftphone()?.toggleMute(), []);
  const toggleHold = useCallback(() => getSoftphone()?.toggleHold(), []);
  const transfer = useCallback(() => getSoftphone()?.showTransferDialog(), []);
  // Re-pop the InCallModal after the rep minimized it. The modal listens
  // for this event; same dispatch path when the contact name is clicked.
  const expand = useCallback(() => {
    window.dispatchEvent(new CustomEvent('apex:incall-modal-show'));
  }, []);
  const completeTransfer = useCallback(() => {
    void getSoftphone()?.completeWarmTransfer();
  }, []);
  const cancelTransfer = useCallback(() => {
    void getSoftphone()?.cancelTransfer();
  }, []);
  // Send the tone server-side (cmd.dtmf targets the contact leg — see
  // client/lib/webrtc.ts for why we don't use browser sendDigits) and
  // echo it in the readout, keeping the last 12 like the InCallModal.
  const pressDigit = useCallback((digit: string) => {
    void getSoftphone()
      ?.sendDtmf(digit)
      .catch(() => {});
    setPressedDigits((prev) => (prev + digit).slice(-12));
  }, []);

  // phase === 'ended' is the 2-second retention window after call.ended fires;
  // the widget should dismiss immediately on terminal, not stay visible until
  // the softphone's cleanup setTimeout nulls the call state.
  if (!call || call.phase === 'ended') return null;

  // Ambiguous caller and the rep hasn't picked who it is in the modal yet →
  // show the count, not one arbitrary name. Picking in the modal flips
  // contactSelected on the shared call, and this falls back to the name.
  const fallbackNumber = call.direction === 'inbound' ? call.from : call.to;
  const displayName =
    matchCount > 1 && !call.contactSelected
      ? `${matchCount} contacts`
      : call.contactName || fallbackNumber;
  const isRinging = call.phase === 'ringing' && call.direction === 'inbound';
  const isConnected = call.phase === 'connected';
  const isDialing =
    call.phase === 'dialing' || (call.phase === 'ringing' && call.direction === 'outbound');

  // Incoming ring — show answer/reject
  if (isRinging) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-brand-green/10 border border-brand-green/20 animate-pulse">
        <PhoneIncoming className="w-4 h-4 text-brand-green" />
        <span className="text-[0.75rem] font-medium text-brand-green truncate max-w-[120px]">
          {displayName}
        </span>
        <button
          onClick={answer}
          className="w-7 h-7 rounded-lg bg-brand-green flex items-center justify-center hover:bg-brand-green/90 transition-colors cursor-pointer"
          title="Answer"
        >
          <Phone className="w-3.5 h-3.5 text-white" />
        </button>
        <button
          onClick={reject}
          className="w-7 h-7 rounded-lg bg-destructive flex items-center justify-center hover:bg-destructive/90 transition-colors cursor-pointer"
          title="Reject"
        >
          <PhoneOff className="w-3.5 h-3.5 text-white" />
        </button>
      </div>
    );
  }

  // Active call (dialing or connected)
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${
        call.held ? 'bg-amber-500/10 border-amber-500/20' : 'bg-primary/5 border-primary/20'
      }`}
    >
      {/* Recording indicator */}
      {recording === 'recording' && (
        <Circle className="w-2.5 h-2.5 fill-destructive text-destructive animate-pulse flex-shrink-0" />
      )}

      {/* Direction icon */}
      <Phone
        className={`w-3.5 h-3.5 flex-shrink-0 ${isDialing ? 'text-amber-500 animate-pulse' : 'text-primary'}`}
      />

      {/* Contact name + timer + our outbound number (#483).
          On outbound calls, displayName already shows the contact side;
          call.from is the user's originating line and was previously
          hidden. Rule #2 says the from must be visible — surface it
          as a compact secondary meta line. */}
      <div className="flex flex-col min-w-0">
        <span className="text-[0.6875rem] font-medium text-foreground truncate max-w-[100px]">
          {displayName}
        </span>
        <span className="text-[0.625rem] text-muted-foreground">
          {call.phase === 'rejoining' ? (
            'Reconnecting…'
          ) : isDialing ? (
            'Dialing...'
          ) : call.held ? (
            'On Hold'
          ) : call.answeredAt ? (
            <CallElapsed answeredAt={call.answeredAt} />
          ) : (
            '0:00'
          )}
          {call.direction === 'outbound' && call.from && call.phase !== 'rejoining' && (
            <span className="font-mono"> · from {call.from}</span>
          )}
        </span>
      </div>

      {/* Transfer badge — shows the active transfer type next to the name */}
      {call.transfer && (
        <span className="text-[0.5625rem] font-bold text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded uppercase">
          {call.transfer.type}
        </span>
      )}

      {/* Controls during a live transfer — Complete (warm only) + Cancel.
          Swaps out Mute/Hold/Transfer because those actions aren't
          meaningful while the call is mid-handoff: the initiator is on
          hold, the target is ringing, and the useful verbs are the two
          that resolve the transfer. Hangup stays available as a last-
          resort teardown below. */}
      {isConnected && call.transfer && (
        <div className="flex items-center gap-1 ml-1">
          {call.transfer.type === 'warm' && (
            <button
              onClick={completeTransfer}
              className="h-7 px-2 rounded-lg bg-brand-green text-white text-[0.6875rem] font-semibold hover:bg-brand-green/90 transition-colors cursor-pointer flex items-center gap-1"
              title="Complete transfer — drop out, leave target + caller connected"
            >
              <Check className="w-3 h-3 stroke-[3]" />
              Complete
            </button>
          )}
          <button
            onClick={cancelTransfer}
            className="h-7 px-2 rounded-lg bg-secondary text-foreground text-[0.6875rem] font-semibold hover:bg-secondary/80 transition-colors cursor-pointer flex items-center gap-1 border border-border"
            title="Cancel transfer — dismiss target, return to caller"
          >
            <X className="w-3 h-3 stroke-[3]" />
            Cancel
          </button>
        </div>
      )}

      {/* Controls during a normal connected call (no active transfer). */}
      {isConnected && !call.transfer && (
        <div className="flex items-center gap-0.5 ml-1">
          <button
            onClick={toggleMute}
            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
              call.muted
                ? 'bg-destructive/10 text-destructive'
                : 'hover:bg-secondary text-muted-foreground'
            }`}
            title={call.muted ? 'Unmute' : 'Mute'}
          >
            {call.muted ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
          </button>
          <button
            onClick={toggleHold}
            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
              call.held
                ? 'bg-amber-500/10 text-amber-600'
                : 'hover:bg-secondary text-muted-foreground'
            }`}
            title={call.held ? 'Resume' : 'Hold'}
          >
            {call.held ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          </button>
          <button
            onClick={transfer}
            className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-secondary text-muted-foreground transition-colors cursor-pointer"
            title="Transfer"
          >
            <ArrowRightLeft className="w-3 h-3" />
          </button>

          {/* DTMF keypad (#787) — collapsed by default; expands as a
              dropdown below the strip so reps can send tones for IVR
              menus, voicemail PINs, and extensions without opening the
              full InCallModal. */}
          <div className="relative" ref={keypadRef}>
            <button
              onClick={() => setKeypadOpen((v) => !v)}
              className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
                keypadOpen
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-secondary text-muted-foreground'
              }`}
              title={keypadOpen ? 'Hide keypad' : 'Keypad — send tones for IVR menus'}
              aria-label={keypadOpen ? 'Hide keypad' : 'Show keypad'}
              aria-expanded={keypadOpen}
            >
              <Grid3x3 className="w-3 h-3" />
            </button>
            {keypadOpen && (
              <div className="absolute top-full right-0 mt-2 w-[180px] bg-card border border-border/80 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-50 p-2 flex flex-col gap-2">
                {/* Readout — last digits sent on this call */}
                <div className="text-[0.625rem] font-mono text-muted-foreground tabular-nums min-h-[14px] truncate text-center">
                  {pressedDigits || '—'}
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {KEYPAD_DIGITS.map((k) => (
                    <button
                      key={k.digit}
                      onClick={() => pressDigit(k.digit)}
                      className="aspect-square rounded-lg bg-secondary/40 hover:bg-secondary text-foreground transition-colors cursor-pointer flex flex-col items-center justify-center"
                    >
                      <span className="text-[0.875rem] font-semibold leading-none">{k.digit}</span>
                      {k.sub && (
                        <span className="text-[0.5rem] text-muted-foreground tracking-wider mt-0.5">
                          {k.sub}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expand to InCallModal — quick way back to the full on-call surface
          after the rep minimized it. */}
      <button
        onClick={expand}
        className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-secondary text-muted-foreground transition-colors cursor-pointer ml-1"
        title="Expand on-call view"
      >
        <Maximize2 className="w-3 h-3" />
      </button>

      {/* Hangup — always available as the tear-down-everything escape
          hatch, including mid-transfer for cold (where Cancel is the
          only other option) or if Complete/Cancel themselves get stuck. */}
      <button
        onClick={hangup}
        className="w-7 h-7 rounded-lg bg-destructive flex items-center justify-center hover:bg-destructive/90 transition-colors cursor-pointer ml-1"
        title="End call"
      >
        <PhoneOff className="w-3.5 h-3.5 text-white" />
      </button>
    </div>
  );
});
